import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import SearchInput from "../../../common/InlineSearch";
import DataTable from "../../../common/DataTable";
import CustomPagination from "../../../common/Pagination";
import TableRowsPerPage from "../../../common/TableRowsPerPage";
import useBreadcrumbs from "../../../hooks/useBreadCrumbs";
import NavCrumbs from "../../../common/NavCrumbs";
import { STAKEHOLDERS_HEADERS } from "./actions/Constant";
import AddStakeholderDetails from "./components/StakeholderHeader";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/Store";
import Stack from "react-bootstrap/Stack";
import { Container } from "react-bootstrap";
import { fetchStakeholderList } from "../../../services/StakeholderApi";
import Loader from "../../../common/Loader";
import { PARTIES_MASTER_ACTIONS } from "../../../common/EventConstant";

const Stakeholder: React.FC = () => {
  // Fetch breadcrumbs for navigation
  const breadcrumbs = useBreadcrumbs();
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  // State for managing user data, search, and pagination
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [open, setOpen] = useState<boolean>(false);
  const [action, setAction] = useState<string>("");
  const [selectedUserData, setSelectedUserData] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchQuerylength, setSearchQuerylength] = useState<string>("");
  const searchInputRef = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<any>("");
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("DESC");

  const allowedFeatures = useSelector(
    (state: RootState) => state.featureAccess.allowedFeatures
  );

  const canAddContact = allowedFeatures.has(PARTIES_MASTER_ACTIONS.CONTACT_ADD);

  // Fetch Stakeholder data from the API
  const fetchStakeholderdata = async (
    query: string = "",
    pageNo: number = 1,
    pageSize: number = itemsPerPage,
    key: string = sortColumn,
    direction: string = sortOrder
  ) => {
    const trimmedQuery = query?.trim();
    const filterParams = {
      searchString: trimmedQuery,
      pageNo,
      pageSize,
      sortColumn: key,
      sortOrder: direction,
      triggerById: loggedInUserid,
    };
    setLoading(true);
    try {
      const stakeholderData = await fetchStakeholderList(filterParams);
      if (stakeholderData) {
        setData(stakeholderData);
        setFilteredData(stakeholderData);
        setTotalPages(stakeholderData[0]?.totalpages || 1);
        setTotalRows(stakeholderData[0]?.totalcount || null);
      } else {
        console.error("No data found in stakeholderData.result");
      }
    } catch (error) {
      console.error("Error fetching stakeholderData:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount and when itemsPerPage or currentPage changes
  useEffect(() => {
    fetchStakeholderdata(
      searchQuery,
      currentPage,
      itemsPerPage,
      sortColumn,
      sortOrder
    );
  }, [itemsPerPage, currentPage]);

  // Handle actions like Edit/View on a row
  const handleRowAction = (row: any, action: string) => {
    setAction(action);
    setOpen(true);
    setSelectedUserData(row);
  };

  // Handle sorting actions in the DataTable
  const handleSortAction = (row: any) => {
    setSortColumn(row.key);
    setSortOrder(row.direction);
    fetchStakeholderdata(
      searchQuery,
      currentPage,
      itemsPerPage,
      row.key,
      row.direction
    );
  };

  // Handle the "Add User" button click
  const handleAddUserClick = () => {
    setAction("Add");
    setSelectedUserData(null);
    setOpen(true);
  };

  // Close Add/Edit form
  const handleCancel = () => {
    setOpen(false);
    setCurrentPage(1);
    handleReset();
  };

  // Reset the user list and search filters
  const handleReset = () => {
    searchInputRef.current?.clear();
    setSearchQuery("");
    setSearchQuerylength("");
    fetchStakeholderdata();
  };

  // Handle pagination
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    fetchStakeholderdata(
      searchQuery,
      pageNumber,
      itemsPerPage,
      sortColumn,
      sortOrder
    );
  };

  // Update the number of rows displayed per page
  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Handle search query change
  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    fetchStakeholderdata(query, 1, itemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchQueryChangeForlength = (query: string) => {
    setSearchQuerylength(query?.trim());
  };

  const entriesText =
    !totalRows
      ? "Showing 0 of 0 entries"
      : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
          currentPage * itemsPerPage,
          totalRows
        )} of ${totalRows} entries`;

  return (
    <div>
      {/* <Loader isLoading={loading} /> */}
      <div className="breadcrumbg">
        <Container fluid className="px-5">
          <Stack direction="horizontal" gap={2}>
            <div className="pt-0 flex-column d-flex align-items-start fade-in-left">
              <h4>Parties</h4>
              <NavCrumbs breadcrumbs={breadcrumbs} />
            </div>
            <div className="p-2 ms-auto">
              {!open && canAddContact && (
                <button
                  onClick={handleAddUserClick}
                  className="btn btn-primary d-flex align-items-center fade-in-right"
                >
                  <span className="material-icons me-2">add</span>
                  Create Party
                </button>
              )}
            </div>
          </Stack>
        </Container>
      </div>

      {!open ? (
        <>
          <div className="main_wrapper_section">
            <div className="container-fluid px-5">
              <div className="d-flex   py-2 search_closer align-items-center">
                <div className="sectionTitle noborder me-auto">List</div>
                <SearchInput
                  ref={searchInputRef}
                  data={data}
                  searchKeys={[]}
                  onSearchResult={setFilteredData}
                  placeholder="Search"
                  onSearchQueryChange={handleSearchQueryChange}
                  onInputChange={handleSearchQueryChangeForlength}
                />
                <button
                  onClick={handleReset}
                  className="reset_btn"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "5px",
                  }}
                >
                  <span className="material-icons reset_ic0">restart_alt</span>
                </button>
              </div>
              <div className="borderB mb-3"></div>
              {/* Data Table Section */}
             <div className="adjustHeightTb"> 
                <DataTable
                  headers={STAKEHOLDERS_HEADERS}
                  rows={filteredData}
                  onAction={handleRowAction}
                  onSortAction={handleSortAction}
                />
              </div>
              {/* Pagination and Rows Per Page Section */}
              <div className="d-flex justify-content-between align-items-center mt-0">
                {/* Display entries text on the left */}
                <div className="col-md-12">
                  <div className="row">
                    <div className="col-md-3 showingData_wrapper">
                      <span>{entriesText}</span>
                    </div>
                    <div className="col-md-9">
                      <div className="d-flex flex-row-reverse ">
                        <CustomPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={paginate}
                        />
                        <TableRowsPerPage
                          value={itemsPerPage}
                          onSelect={handleRowsPerPageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <AddStakeholderDetails
          userData={selectedUserData}
          action={action}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};

export default Stakeholder;
